import React, { memo, useCallback } from 'react';
import FORM_CONFIG from '../config/formConfig';
import { sinDestinoDeGuardado as calcularSinDestinoDeGuardado } from '../utils/fieldSaveDestination';
import {
  TextField,
  IntegerField,
  DecimalField,
  EmailField,
  PhoneField,
  UrlField,
  TextAreaField
} from './fields/TextFields';
import {
  DateField,
  TimeField,
  DateTimeField
} from './fields/DateTimeFields';
import {
  SelectOneField,
  SelectMultipleField
} from './fields/SelectFields';
import {
  AudioField,
  VideoField,
  FileField
} from './fields/FileFields';
import ImageField from './fields/ImageField';
import {
  NoteField,
  BarcodeField
} from './fields/SpecialFields';

/**
 * Evalúa si un campo debe estar en modo de solo lectura
 * @param {*} configActualizable - El valor de config_actualizable de la pregunta
 * @param {string} modoVisualizacion - Modo de visualización del formulario
 * @param {string} questionName - Nombre de la pregunta (para debug)
 * @returns {boolean} - true si el campo debe ser readonly, false si es editable
 */
const isFieldReadonly = (configActualizable, modoVisualizacion, questionName = '') => {
  // Si el modo de visualización es 'readonly', el campo debe ser readonly
  if (modoVisualizacion === 'readonly') {
    return true;
  }
  
  // Si config_actualizable es explícitamente false (no null/undefined), el campo es readonly
  if (configActualizable === false || configActualizable === 0 || configActualizable === "0") {
    return true;
  }
  
  // En todos los demás casos (incluyendo null, undefined, true, 1, "1"), el campo es editable
  return false;
};

/**
 * Evalúa si un valor es "truthy" para la lógica de config_actualizable
 * @param {*} value - Valor a evaluar
 * @returns {boolean} - true si el valor se considera "truthy"
 */
const isValueTruthy = (value) => {
  if (value === null || value === undefined) {
    return false;
  }
  
  if (typeof value === 'string') {
    const trimmed = value.trim().toLowerCase();
    return trimmed !== '' && trimmed !== 'false' && trimmed !== '0' && trimmed !== 'null';
  }
  
  if (typeof value === 'number') {
    return value !== 0;
  }
  
  if (typeof value === 'boolean') {
    return value;
  }
  
  return Boolean(value);
};

const FormField = memo(({ question, value, onChange, error, modoVisualizacion, handleFileChange }) => {
  // Detectar si el readonly proviene de bloqueo por administrador (config_actualizable)
  const readonlyByAdmin = (question?.config_actualizable === false || question?.config_actualizable === 0 || question?.config_actualizable === "0");
  // Detectar si el campo no tiene dónde guardarse (config_tabla/config_columna vacíos): el valor
  // se vería editable pero nunca llega a persistirse, así que debe quedar bloqueado siempre.
  // El aviso al usuario se muestra como ícono (i) junto al label, en GeneradorFormulario.
  const sinDestinoDeGuardado = calcularSinDestinoDeGuardado(question);

  const renderField = () => {
    // Obtener el tipo base del campo (separado por espacio)
    let _type = question.type.split(' ')[0];

    // Evaluar si el campo debe ser readonly
    const readonly = isFieldReadonly(question.config_actualizable, modoVisualizacion, question.name) || sinDestinoDeGuardado;

    const commonProps = {
      question,
      value,
      onChange,
      error,
      readonly
    };

    // Extraer el nombre del catálogo del tipo para select_one y select_multiple
    const extraerCatalogo = (tipo) => {
      const partes = tipo.split(' ');
      return partes.length > 1 ? partes[1] : null;
    };

    switch (_type) {
      case 'text':
      case 'string':
        return <TextField {...commonProps} />;

      case 'integer':
        return <IntegerField {...commonProps} />;

      case 'decimal':
        return <DecimalField {...commonProps} />;

      case 'date':
        return <DateField {...commonProps} />;

      case 'time':
        return <TimeField {...commonProps} />;

      case 'datetime':
        return <DateTimeField {...commonProps} />;

      case 'select_one':
        const catalogReferenceOne = extraerCatalogo(question.type);
        const questionWithCatalogOne = {
          ...question,
          catalogReference: catalogReferenceOne
        };
        return <SelectOneField {...commonProps} question={questionWithCatalogOne} />;

      case 'select_multiple':
        const catalogReferenceMultiple = extraerCatalogo(question.type);
        const questionWithCatalogMultiple = {
          ...question,
          catalogReference: catalogReferenceMultiple
        };
        return <SelectMultipleField {...commonProps} question={questionWithCatalogMultiple} />;

      case 'note':
        return <NoteField question={question} />;

      case 'textarea':
        return <TextAreaField {...commonProps} />;

      case 'email':
        return <EmailField {...commonProps} />;

      case 'tel':
      case 'phone':
        return <PhoneField {...commonProps} />;

      case 'url':
        return <UrlField {...commonProps} />;

      case 'geopoint':
        // Sin componente de mapa disponible: la pregunta no se renderiza
        return null;
      case 'image':
        return (
          <ImageField
            {...commonProps}
            handleFileChange={handleFileChange}
          />
        );

      case 'audio':
        return (
          <AudioField
            {...commonProps}
            handleFileChange={handleFileChange}
          />
        );

      case 'video':
        return (
          <VideoField
            {...commonProps}
            handleFileChange={handleFileChange}
          />
        );

      case 'file':
        return (
          <FileField
            {...commonProps}
            handleFileChange={handleFileChange}
          />
        );

      case 'barcode':
        return <BarcodeField {...commonProps} />;

      default:
        // Manejar tipos dinámicos como "select_one sino" o "select_multiple opciones"
        if (question.type.startsWith('select_one')) {
          const catalogReference = extraerCatalogo(question.type);
          const questionWithCatalog = {
            ...question,
            catalogReference: catalogReference
          };
          return <SelectOneField {...commonProps} question={questionWithCatalog} />;
        }
        
        if (question.type.startsWith('select_multiple')) {
          const catalogReference = extraerCatalogo(question.type);
          const questionWithCatalog = {
            ...question,
            catalogReference: catalogReference
          };
          return <SelectMultipleField {...commonProps} question={questionWithCatalog} />;
        }

        // Fallback para tipos no reconocidos
        return <TextField {...commonProps} />;
    }
  };

  return (
    <div className="form-field">
      {renderField()}
      {!sinDestinoDeGuardado && readonlyByAdmin && FORM_CONFIG?.readonlyNote?.enabled && (
        <div
          className="field-block-note"
          style={{
            marginTop: 8,
            fontSize: '12px',
            color: '#555',
            padding: '8px',
            backgroundColor: '#f9fafb',
            borderLeft: '4px solid #9e9e9e',
            borderRadius: 4
          }}
        >
          {FORM_CONFIG?.readonlyNote?.text || 'Este campo está bloqueado para su edición por el administrador'}
        </div>
      )}
    </div>
  );
});

FormField.displayName = 'FormField';

export default FormField;